import React, { useState } from 'react';
import { Modal, Input, Button } from '../../../../components/ui';

export interface ClientFormData {
  name: string;
  email: string;
  contactPerson: string;
}

interface FormErrors {
  name?: string;
  email?: string;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: ClientFormData) => Promise<boolean>;
  loading?: boolean;
}

export const ClientModal: React.FC<Props> = ({ isOpen, onClose, onSubmit, loading }) => {
  const [formData, setFormData] = useState<ClientFormData>({
    name: '',
    email: '',
    contactPerson: ''
  });

  const [errors, setErrors] = useState<FormErrors>({});

  const handleChange = (field: keyof ClientFormData) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [field]: e.target.value
    });
    if (errors[field as keyof FormErrors]) {
      setErrors({
        ...errors,
        [field]: undefined
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Client name is required';
    }

    if (formData.email.trim()) {
      if (!/\S+@\S+\.\S+/.test(formData.email)) {
        newErrors.email = 'Email is invalid';
      }
    } else {
      newErrors.email = 'Email is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const success = await onSubmit(formData);

    if (success) {
      setFormData({
        name: '',
        email: '',
        contactPerson: ''
      });
      setErrors({});
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      email: '',
      contactPerson: ''
    });
    setErrors({});
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Create New Client"
    >
      <form onSubmit={handleSubmit}>
        <Input
          label="Client Name"
          value={formData.name}
          onChange={handleChange('name')}
          placeholder="Enter client organization name"
          required
          error={errors.name}
          className="mb-4"
        />

        <Input
          label="Contact Email"
          type="email"
          value={formData.email}
          onChange={handleChange('email')}
          placeholder="contact@example.com"
          required
          error={errors.email}
          className="mb-4"
        />

        <Input
          label="Contact Person"
          value={formData.contactPerson}
          onChange={handleChange('contactPerson')}
          placeholder="John Doe (optional)"
          className="mb-6"
        />

        <div className="flex justify-end space-x-3">
          <Button type="button" onClick={handleClose} variant="secondary" disabled={loading}>
            Cancel
          </Button>
          <Button type="submit" loading={loading}>
            Create Client
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default ClientModal;
